import { useEffect, useState } from "react";
import { Link, NavLink, Outlet, useLocation } from "react-router-dom";
import { Menu, X, Phone, MapPin, Mail, ShieldCheck } from "lucide-react";

const NAV = [
  { to: "/", label: "Home", end: true },
  { to: "/about", label: "About Us" },
  { to: "/executive-body", label: "Executive Body" },
  { to: "/gallery", label: "Gallery" },
  { to: "/notices", label: "Notices" },
  { to: "/payment", label: "Payment" },
  { to: "/contact", label: "Contact" },
];

function Header() {
  const [open, setOpen] = useState(false);
  const loc = useLocation();
  useEffect(() => setOpen(false), [loc.pathname]);

  return (
    <header className="sticky top-0 z-50 shadow-lg" data-testid="site-header">
      <div className="gold-line h-1 w-full" />
      {/* thin contact strip */}
      <div className="bg-[#061733] text-[#c7d2e6] text-xs border-b border-[#123056]">
        <div className="max-w-7xl mx-auto px-4 py-1.5 flex items-center justify-center gap-2">
          <span className="flex items-center gap-2 font-garamond text-sm tracking-wide">
            <MapPin size={13} /> First Floor, High Court Campus, Jabalpur, Madhya Pradesh
          </span>
        </div>
      </div>

      {/* centered brand + horizontal nav (Bombay-Bar style) */}
      <div className="bg-[#071B3D] border-b border-[#123056] relative">
        <div className="max-w-7xl mx-auto px-4 py-5 flex flex-col items-center">
          <Link to="/" className="flex items-center gap-4 justify-center text-center" data-testid="header-logo">
            <img src="/logo.jpg" alt="HC ABA emblem" className="w-16 h-16 rounded-full object-cover ring-2 ring-[#D4AF37]/60 shadow" />
            <div className="leading-tight">
              <div className="font-display text-[#F3EAD6] font-semibold text-xl sm:text-2xl lg:text-[28px] tracking-[0.12em] uppercase">High Court Advocates' Bar Association</div>
              <div className="text-[10px] sm:text-[11px] tracking-[0.35em] uppercase text-[#D4AF37] font-semibold mt-1">Jabalpur · Madhya Pradesh</div>
            </div>
          </Link>

          <button className="lg:hidden absolute right-4 top-6 text-[#F3EAD6]" onClick={() => setOpen(!open)} data-testid="mobile-menu-toggle">
            {open ? <X /> : <Menu />}
          </button>

          <div className="hidden lg:flex flex-col items-center mt-6 gap-2">
            <nav className="flex items-center justify-center gap-x-8">
              {NAV.slice(0, 6).map((n) => (
                <NavLink key={n.to} to={n.to} end={n.end}
                  className={({ isActive }) => `nav-underline text-[13px] font-medium tracking-[0.18em] uppercase text-[#cdd6e6] hover:text-[#D4AF37] transition-colors ${isActive ? "active text-[#D4AF37]" : ""}`}
                  data-testid={`nav-${n.label.toLowerCase().replace(/\s+/g, "-")}`}>
                  {n.label}
                </NavLink>
              ))}
            </nav>
            <nav className="flex items-center justify-center gap-x-8">
              {NAV.slice(6).map((n) => (
                <NavLink key={n.to} to={n.to} end={n.end}
                  className={({ isActive }) => `nav-underline text-[13px] font-medium tracking-[0.18em] uppercase text-[#cdd6e6] hover:text-[#D4AF37] transition-colors ${isActive ? "active text-[#D4AF37]" : ""}`}
                  data-testid={`nav-${n.label.toLowerCase().replace(/\s+/g, "-")}`}>
                  {n.label}
                </NavLink>
              ))}
              <Link to="/admin/login" className="flex items-center gap-1.5 text-[11px] font-semibold tracking-[0.12em] uppercase text-[#071B3D] bg-[#D4AF37] rounded-full px-3 py-1.5 hover:brightness-105 transition" data-testid="nav-admin">
                <ShieldCheck size={13} /> Admin
              </Link>
            </nav>
          </div>
        </div>
      </div>

      {open && (
        <nav className="lg:hidden bg-[#071B3D] border-b border-[#123056] px-4 py-3 flex flex-col gap-1" data-testid="mobile-nav">
          {NAV.map((n) => (
            <NavLink key={n.to} to={n.to} end={n.end}
              className={({ isActive }) => `py-2 px-2 rounded text-sm font-medium tracking-wide uppercase ${isActive ? "bg-[#123056] text-[#D4AF37]" : "text-[#cdd6e6]"}`}>
              {n.label}
            </NavLink>
          ))}
          <Link to="/admin/login" className="py-2 px-2 text-sm font-semibold uppercase tracking-wide text-[#D4AF37]">Admin Login</Link>
        </nav>
      )}
    </header>
  );
}

function Footer() {
  return (
    <footer className="bg-[#061733] text-[#d8c9a8] mt-20" data-testid="site-footer">
      <div className="gold-line h-1 w-full" />
      <div className="max-w-7xl mx-auto px-4 py-12 grid md:grid-cols-3 gap-10">
        <div>
          <div className="flex items-center gap-3 mb-4">
            <img src="/logo.jpg" alt="emblem" className="w-12 h-12 rounded-full object-cover" />
            <div className="font-display text-white font-semibold">HCABA, Jabalpur</div>
          </div>
          <p className="font-garamond text-sm leading-relaxed text-[#c9b998]">
            Upholding the dignity of the legal profession and serving the advocates practising before the High Court of Madhya Pradesh at Jabalpur.
          </p>
        </div>
        <div>
          <h4 className="font-display text-[#D4AF37] mb-3">Quick Links</h4>
          <ul className="space-y-2 text-sm">
            {NAV.map((n) => (
              <li key={n.to}><Link to={n.to} className="hover:text-[#D4AF37] transition-colors">{n.label}</Link></li>
            ))}
          </ul>
        </div>
        <div>
          <h4 className="font-display text-[#D4AF37] mb-3">Contact</h4>
          <p className="text-sm flex items-start gap-2 mb-2"><MapPin size={15} className="mt-0.5" /> First Floor, High Court Campus, Jabalpur, Madhya Pradesh</p>
          <p className="text-sm flex items-center gap-2 mb-2"><Phone size={15} /> 0761 2626 219 (Landline)</p>
          <p className="text-sm flex items-center gap-2 mb-2"><Phone size={15} /> 9907267718 (Mobile)</p>
          <p className="text-sm flex items-center gap-2"><Mail size={15} /> hcba341@gmail.com</p>
        </div>
      </div>
      <div className="border-t border-[#123056] py-4 text-center text-xs text-[#a8987a]">
        © {new Date().getFullYear()} High Court Advocates' Bar Association, Jabalpur. All rights reserved.
      </div>
    </footer>
  );
}

export default function PublicLayout() {
  return (
    <div className="min-h-screen flex flex-col">
      <Header />
      <main className="flex-1"><Outlet /></main>
      <Footer />
    </div>
  );
}
